import { devError } from '../utils/devLogger';
import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  Alert,
  Spinner
} from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import issuesService from '../services/issuesService';
import { exportIssueReportToPDF } from '../utils/pdfExport';
import AdminNavbar from '../components/AdminNavbar';
import FacilitatorNavbar from '../components/FacilitatorNavbar';
import QANavbar from '../components/QANavbar';
import '../styles/brand.css';

const IssueTracker = () => {
  usePageTitle("Issue Tracker");
  const { currentUser, userProfile } = useAuth();
  const [allIssues, setAllIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueComments, setIssueComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = useMemo(() => issuesService.getCategories(), []);
  const priorities = useMemo(() => issuesService.getPriorities(), []);
  const statuses = useMemo(() => issuesService.getStatuses(), []);

  useEffect(() => {
    const unsubscribe = issuesService.subscribeToAllIssues((issues) => {
      setAllIssues(issues || []);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let issues = [...allIssues];

    if (selectedCategory !== 'all') {
      issues = issues.filter((issue) => issue.category === selectedCategory);
    }
    if (selectedStatus !== 'all') {
      issues = issues.filter((issue) => issue.status === selectedStatus);
    }
    if (selectedPriority !== 'all') {
      issues = issues.filter((issue) => issue.priority === selectedPriority);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      issues = issues.filter(
        (issue) =>
          issue.title?.toLowerCase()?.includes(term) ||
          issue.description?.toLowerCase()?.includes(term) ||
          issue.reportedByName?.toLowerCase()?.includes(term)
      );
    }

    const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
    issues.sort((a, b) => {
      const priorityDifference = (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
      if (priorityDifference !== 0) return priorityDifference;

      const aDate = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const bDate = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return bDate - aDate;
    });

    setFilteredIssues(issues);
  }, [allIssues, selectedCategory, selectedStatus, selectedPriority, searchTerm]);

  useEffect(() => {
    if (!showDetailModal || !selectedIssue?.id) {
      setIssueComments([]);
      setLoadingComments(false);
      setCommentError('');
      return undefined;
    }

    setLoadingComments(true);
    setCommentError('');
    const unsubscribe = issuesService.subscribeToIssueComments(
      selectedIssue.id,
      (comments) => {
        setIssueComments(comments || []);
        setLoadingComments(false);
      },
      (commentsError) => {
        setCommentError(commentsError.message || 'Failed to load comments.');
        setLoadingComments(false);
      }
    );

    return () => unsubscribe();
  }, [showDetailModal, selectedIssue?.id]);

  const handleCreateIssue = () => {
    setEditingIssue({
      title: '',
      description: '',
      priority: 'medium',
      category: 'technical',
      status: 'open'
    });
    setShowIssueModal(true);
  };

  const handleEditIssue = (issue) => {
    setEditingIssue(issue);
    setShowIssueModal(true);
  };

  const handleViewIssue = (issue) => {
    setSelectedIssue(issue);
    setShowDetailModal(true);
    setNewComment('');
    setIssueComments([]);
    setCommentError('');
  };

  const handleSaveIssue = async () => {
    if (!editingIssue?.title || !editingIssue?.description) {
      setError('Please provide a title and description for the issue.');
      return;
    }

    try {
      if (editingIssue.id) {
        await issuesService.updateIssue(editingIssue.id, {
          title: editingIssue.title,
          description: editingIssue.description,
          priority: editingIssue.priority,
          category: editingIssue.category,
          status: editingIssue.status,
          assignedTo: editingIssue.assignedTo || null,
          assignedToName: editingIssue.assignedToName || null
        });
      } else {
        await issuesService.createIssue({
          ...editingIssue,
          reportedBy: currentUser?.uid || 'system',
          reportedByName: currentUser?.displayName || currentUser?.email || 'System User',
          reportedByEmail: currentUser?.email || '',
          createdAt: new Date().toISOString()
        });
      }

      setShowIssueModal(false);
      setEditingIssue(null);
      setError(null);
    } catch (saveError) {
      // eslint-disable-next-line no-console
      devError('IssueTracker: failed to save issue', saveError);
      setError(saveError.message || 'Failed to save issue');
    }
  };

  const handleAddComment = async () => {
    if (!selectedIssue || !newComment.trim()) return;
    if (!currentUser?.uid) {
      setCommentError('You must be signed in to add a comment.');
      return;
    }

    try {
      setCommentSubmitting(true);
      setCommentError('');
      const comment = await issuesService.addComment(selectedIssue.id, {
        author: currentUser.uid,
        authorName: currentUser.displayName || currentUser.email,
        content: newComment.trim()
      });

      setSelectedIssue({
        ...selectedIssue,
        comments: [...(selectedIssue.comments || []), comment]
      });
      setNewComment('');
    } catch (commentError) {
      // eslint-disable-next-line no-console
      devError('IssueTracker: failed to add comment', commentError);
      setCommentError(commentError.message || 'Failed to add comment.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleExportIssues = () => {
    exportIssueReportToPDF(filteredIssues, 'Issue Tracker Report');
  };

  const getNavbar = () => {
    if (userProfile?.role === 'admin') {
      return <AdminNavbar currentPage="issues" />;
    }
    if (userProfile?.role === 'facilitator') {
      return <FacilitatorNavbar currentPage="issues" />;
    }
    if (userProfile?.role === 'qa') {
      return <QANavbar />;
    }
    return null;
  };

  const getStatusBadge = (status) => {
    const statusMeta = statuses.find((item) => item.id === status);
    return (
      <Badge bg={statusMeta?.color || 'secondary'}>
        {statusMeta?.name || status}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityMeta = priorities.find((item) => item.id === priority);
    return (
      <Badge bg={priorityMeta?.color || 'secondary'}>
        {priorityMeta?.name || priority}
      </Badge>
    );
  };

  const canManageIssues = ['admin', 'facilitator'].includes(userProfile?.role);
  const visibleComments = [
    ...(selectedIssue?.comments || []),
    ...issueComments
  ].filter((comment, index, comments) => {
    const commentKey = comment.id || `${comment.author || ''}-${comment.content || ''}-${comment.createdAt?.seconds || comment.createdAt || ''}`;
    return comments.findIndex((item) => {
      const itemKey = item.id || `${item.author || ''}-${item.content || ''}-${item.createdAt?.seconds || item.createdAt || ''}`;
      return itemKey === commentKey;
    }) === index;
  });

  return (
    <>
      {getNavbar()}
      <Container fluid className="px-4 py-4">
        <Row className="mb-3">
          <Col>
            <h2 className="mb-0">Issue Tracker</h2>
            <p className="text-muted">Monitor and resolve learner and facilitator issues.</p>
          </Col>
          <Col className="text-end">
            {canManageIssues && (
              <Button variant="primary" className="me-2" onClick={handleCreateIssue}>
                Create Issue
              </Button>
            )}
            <Button variant="outline-secondary" onClick={handleExportIssues}>
              Export Report
            </Button>
          </Col>
        </Row>

        <Card className="shadow-sm border-0 mb-4">
          <Card.Body>
            <Row>
              <Col md={3} className="mb-2">
                <Form.Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                  <option value="all">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3} className="mb-2">
                <Form.Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                  <option value="all">All Statuses</option>
                  {statuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3} className="mb-2">
                <Form.Select value={selectedPriority} onChange={(e) => setSelectedPriority(e.target.value)}>
                  <option value="all">All Priorities</option>
                  {priorities.map((priorityItem) => (
                    <option key={priorityItem.id} value={priorityItem.id}>
                      {priorityItem.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3} className="mb-2">
                <Form.Control
                  type="search"
                  placeholder="Search issues..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {loading ? (
          <Card className="shadow-sm border-0">
            <Card.Body className="text-center text-muted py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading issues...
            </Card.Body>
          </Card>
        ) : filteredIssues.length === 0 ? (
          <Alert variant="info">No issues found matching the selected filters.</Alert>
        ) : (
          <Card className="shadow-sm border-0">
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Issue</th>
                    <th>Reporter</th>
                    <th>Category</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIssues.map((issue) => (
                    <tr key={issue.id}>
                      <td>
                        <strong>{issue.title}</strong>
                        <p className="text-muted small mb-0">{issue.description}</p>
                      </td>
                      <td>
                        <div>{issue.reportedByName || issue.reportedBy}</div>
                        <small className="text-muted">{issue.reportedByEmail}</small>
                      </td>
                      <td>
                        <Badge bg="info">{issue.category || 'general'}</Badge>
                      </td>
                      <td>{getPriorityBadge(issue.priority)}</td>
                      <td>{getStatusBadge(issue.status)}</td>
                      <td>
                        {issue.createdAt?.toDate?.()?.toLocaleString?.() ||
                          (issue.createdAt ? new Date(issue.createdAt).toLocaleString() : '')}
                      </td>
                      <td>
                        <Button variant="link" size="sm" onClick={() => handleViewIssue(issue)}>
                          View
                        </Button>
                        {canManageIssues && (
                          <Button variant="link" size="sm" onClick={() => handleEditIssue(issue)}>
                            Edit
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </Container>

      <Modal show={showIssueModal} onHide={() => setShowIssueModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingIssue?.id ? 'Edit Issue' : 'Create Issue'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={editingIssue?.title || ''}
                onChange={(e) => setEditingIssue({ ...editingIssue, title: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={editingIssue?.description || ''}
                onChange={(e) => setEditingIssue({ ...editingIssue, description: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={editingIssue?.category || 'technical'}
                    onChange={(e) => setEditingIssue({ ...editingIssue, category: e.target.value })}
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    value={editingIssue?.priority || 'medium'}
                    onChange={(e) => setEditingIssue({ ...editingIssue, priority: e.target.value })}
                  >
                    {priorities.map((pri) => (
                      <option key={pri.id} value={pri.id}>
                        {pri.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={editingIssue?.status || 'open'}
                    onChange={(e) => setEditingIssue({ ...editingIssue, status: e.target.value })}
                  >
                    {statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowIssueModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveIssue}>
            Save Issue
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Issue Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedIssue ? (
            <>
              <h5>{selectedIssue.title}</h5>
              <p>{selectedIssue.description}</p>
              <div className="d-flex gap-2 mb-3">
                {getStatusBadge(selectedIssue.status)}
                {getPriorityBadge(selectedIssue.priority)}
              </div>
              <h6>Comments</h6>
              {commentError && (
                <Alert variant="danger" className="mb-3">
                  {commentError}
                </Alert>
              )}
              {loadingComments ? (
                <div className="text-muted mb-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading comments...
                </div>
              ) : visibleComments.length > 0 ? (
                <div className="mb-3">
                  {visibleComments.map((comment) => (
                    <div key={comment.id} className="border rounded p-2 mb-2">
                      <div className="d-flex justify-content-between">
                        <strong>{comment.authorName || comment.author}</strong>
                        <small className="text-muted">
                          {comment.createdAt?.toDate?.()?.toLocaleString?.() ||
                            (comment.createdAt ? new Date(comment.createdAt).toLocaleString() : '')}
                        </small>
                      </div>
                      <p className="mb-0">{comment.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="light">No comments yet.</Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Add Comment</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
              </Form.Group>
            </>
          ) : (
            <Spinner animation="border" size="sm" />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleAddComment}
            disabled={commentSubmitting || !newComment.trim()}
          >
            {commentSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : 'Add Comment'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default IssueTracker;
